import type { PageMeta } from "@/marketing/catalogBase";
import { comparisonFaqs, procurementSoftwareGuideFaqs, priorityCommercialPage, type SeoFaq } from "@/marketing/seo/priorityContent";

const ORIGIN = "https://raqoz.com";
const COMPANY = "https://www.cyclotrontechnologies.com";
const IDS = { organization: `${ORIGIN}/#organization`, website: `${ORIGIN}/#website`, software: `${ORIGIN}/#software` };

function url(path: string) { return `${ORIGIN}${path === "/" ? "" : path}`; }
function label(segment: string) { return segment.replaceAll("-", " ").replace(/\b\w/g, char => char.toUpperCase()); }

function breadcrumb(path: string) {
  if (path === "/") return null;
  const parts = path.split("/").filter(Boolean);
  return {
    "@type": "BreadcrumbList",
    "@id": `${url(path)}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: ORIGIN },
      ...parts.map((part, index) => ({
        "@type": "ListItem",
        position: index + 2,
        name: label(part),
        item: url(`/${parts.slice(0, index + 1).join("/")}`),
      })),
    ],
  };
}

function faq(items: SeoFaq[], path: string) {
  return {
    "@type": "FAQPage",
    "@id": `${url(path)}#faq`,
    mainEntity: items.map(([question, answer]) => ({ "@type": "Question", name: question, acceptedAnswer: { "@type": "Answer", text: answer } })),
  };
}

export function structuredDataForPage(meta: PageMeta) {
  const canonicalPath = meta.canonicalPath || meta.path;
  const graph: Record<string, unknown>[] = [];
  if (meta.path === "/") {
    graph.push(
      { "@type": "Organization", "@id": IDS.organization, name: "Cyclotron Technologies", url: COMPANY, brand: { "@type": "Brand", name: "RAQOZ" }, logo: { "@type": "ImageObject", url: `${ORIGIN}/favicon-32.png` } },
      { "@type": "WebSite", "@id": IDS.website, name: "RAQOZ", url: ORIGIN, publisher: { "@id": IDS.organization }, inLanguage: "en" },
      { "@type": "SoftwareApplication", "@id": IDS.software, name: "RAQOZ", applicationCategory: "BusinessApplication", operatingSystem: "Web", url: ORIGIN, description: meta.description, publisher: { "@id": IDS.organization } },
    );
  }
  graph.push({
    "@type": meta.path === "/product" || meta.path === "/pricing" ? "SoftwareApplication" : "WebPage",
    "@id": `${url(canonicalPath)}#page`,
    name: meta.title,
    description: meta.description,
    url: url(canonicalPath),
    isPartOf: { "@id": IDS.website },
    publisher: { "@id": IDS.organization },
    ...(meta.path === "/product" || meta.path === "/pricing" ? { applicationCategory: "BusinessApplication", operatingSystem: "Web" } : {}),
  });
  const crumbs = breadcrumb(canonicalPath);
  if (crumbs) graph.push(crumbs);

  const priority = priorityCommercialPage(meta.path);
  if (priority) graph.push(faq(priority.faqs, meta.path));
  if (meta.path === "/guides/what-is-procurement-software") {
    graph.push({ "@type": "Article", "@id": `${url(meta.path)}#article`, headline: "What is procurement software?", datePublished: "2026-09-02", dateModified: "2026-09-02", author: { "@type": "Organization", name: "RAQOZ Editorial Team", url: url("/authors/raqoz-editorial-team") }, reviewedBy: { "@type": "Organization", name: "Product & Evidence Review", url: url("/authors/product-evidence-review") }, publisher: { "@id": IDS.organization }, mainEntityOfPage: { "@id": `${url(meta.path)}#page` } }, faq(procurementSoftwareGuideFaqs, meta.path));
  }
  if (meta.path === "/compare/best-procurement-software-india") {
    graph.push({ "@type": "Article", "@id": `${url(meta.path)}#article`, headline: "Best procurement software in India for 2026", datePublished: "2026-09-02", dateModified: "2026-09-02", author: { "@type": "Organization", name: "RAQOZ Editorial Team", url: url("/authors/raqoz-editorial-team") }, reviewedBy: { "@type": "Organization", name: "Product & Evidence Review", url: url("/authors/product-evidence-review") }, publisher: { "@id": IDS.organization }, mainEntityOfPage: { "@id": `${url(meta.path)}#page` } }, faq(comparisonFaqs, meta.path));
  }
  if (meta.path === "/editorial-policy") graph.push({ "@type": "Article", headline: meta.title, datePublished: "2026-09-02", dateModified: "2026-09-02", author: { "@type": "Organization", name: "RAQOZ Editorial Team" }, publisher: { "@id": IDS.organization }, mainEntityOfPage: { "@id": `${url(meta.path)}#page` } });
  return { "@context": "https://schema.org", "@graph": graph };
}
